import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore, selectIncomeUnits, selectBusinesses, selectProjects, selectPersonalProjects } from '@/utils/store';
import { CATEGORY_ICONS } from '@/utils/format';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import CurrencyInput from '@/components/ui/CurrencyInput';
import { useFormDraft } from '@/hooks/useFormDraft';
import MemberMultiSelect from '@/components/ui/MemberMultiSelect';
import { Camera, ChevronDown, Link2, Repeat } from 'lucide-react';
import { useCurrency } from '@/hooks/useCurrency';

const EXPENSE_CATEGORIES = ['food', 'transport', 'housing', 'entertainment', 'health', 'education', 'shopping', 'services', 'credit', 'unit_cost', 'other'];
const INCOME_CATEGORIES = ['salary', 'freelance', 'service', 'other'];

export default function AddTransactionModal({ open, onClose, defaultType = 'expense', transaction = null, onScanReceipt, defaultBusinessId = '', defaultProjectId = '', defaultPropertyId = '', defaultPersonalProjectId = '' }) {
  const { t } = useTranslation();
  const { format: formatCurrency } = useCurrency();
  const addTransaction = useStore((s) => s.addTransaction);
  const updateTransaction = useStore((s) => s.updateTransaction);
  const linkTransactions = useStore((s) => s.linkTransactions);
  const transactions = useStore((s) => s.transactions);
  const incomeUnits = useStore(selectIncomeUnits);
  const businesses = useStore(selectBusinesses);
  const allProjects = useStore(selectProjects);
  const personalProjects = useStore(selectPersonalProjects);
  const businessEnabled = useStore((s) => s.appSettings?.businessEnabled === true);

  const isEdit = !!transaction;
  const draft = useFormDraft('addTransaction');

  const [type, setType] = useState(defaultType);
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [memberIds, setMemberIds] = useState([]);
  const [unitId, setUnitId] = useState('');
  const [linkedToId, setLinkedToId] = useState('');
  const [showLinkSection, setShowLinkSection] = useState(false);
  const [businessId, setBusinessId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [clientName, setClientName] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [personalProjectId, setPersonalProjectId] = useState('');
  const [recurrence, setRecurrence] = useState('none');
  const [errors, setErrors] = useState({});

  // Populate fields when editing or apply defaults/drafts when creating
  useEffect(() => {
    if (!open) return;
    if (transaction) {
      setType(transaction.type || defaultType);
      setAmount(String(transaction.amount || ''));
      setCategory(transaction.category || '');
      setDescription(transaction.description || '');
      setDate(transaction.date || new Date().toISOString().split('T')[0]);
      setMemberIds(transaction.memberIds || []);
      setUnitId(transaction.unitId || '');
      setLinkedToId(transaction.linkedTo || '');
      setShowLinkSection(!!transaction.linkedTo);
      setBusinessId(transaction.businessId || '');
      setProjectId(transaction.projectId || '');
      setClientName(transaction.clientName || '');
      setSupplierName(transaction.supplierName || '');
      setPropertyId(transaction.propertyId || '');
      setPersonalProjectId(transaction.personalProjectId || '');
      setRecurrence(transaction.recurrence || 'none');
      setErrors({});
    } else {
      const saved = draft.load();
      if (saved) {
        setType(saved.type || defaultType);
        setAmount(saved.amount || '');
        setCategory(saved.category || '');
        setDescription(saved.description || '');
        setDate(saved.date || new Date().toISOString().split('T')[0]);
        setMemberIds(saved.memberIds || []);
        setUnitId(saved.unitId || '');
        // Defaults from parent page always take priority over draft
        setBusinessId(defaultBusinessId || saved.businessId || '');
        setProjectId(defaultProjectId || saved.projectId || '');
        setClientName(saved.clientName || '');
        setSupplierName(saved.supplierName || '');
        setPropertyId(defaultPropertyId || saved.propertyId || '');
        setPersonalProjectId(defaultPersonalProjectId || saved.personalProjectId || '');
        setRecurrence(saved.recurrence || 'none');
      } else {
        setType(defaultType);
        setBusinessId(defaultBusinessId);
        setProjectId(defaultProjectId);
        setPropertyId(defaultPropertyId);
        setPersonalProjectId(defaultPersonalProjectId);
      }
      setErrors({});
    }
  }, [transaction, open, defaultType, defaultBusinessId, defaultProjectId, defaultPropertyId, defaultPersonalProjectId]);

  // Auto-save draft
  useEffect(() => {
    if (!open || isEdit) return;
    draft.save({ type, amount, category, description, date, memberIds, unitId, businessId, projectId, clientName, supplierName, propertyId, personalProjectId, recurrence });
  }, [open, isEdit, type, amount, category, description, date, memberIds, unitId, businessId, projectId, clientName, supplierName, propertyId, personalProjectId, recurrence]);

  const categories = type === 'income' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;

  const validate = () => {
    const errs = {};
    if (!amount || Number(amount) <= 0) errs.amount = t('transactions.form.amountRequired');
    if (!category) errs.category = t('transactions.form.categoryRequired');
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    const data = {
      description: description || t(`categories.${category}`),
      amount: Number(amount),
      type,
      category,
      date,
      ...(businessId ? { businessId } : { businessId: undefined }),
      ...(projectId ? { projectId } : { projectId: undefined }),
      ...(!businessId && memberIds.length > 0 ? { memberIds } : { memberIds: [] }),
      ...(unitId && category === 'unit_cost' ? { unitId } : { unitId: undefined }),
      ...(businessId && type === 'income' && clientName ? { clientName } : { clientName: undefined }),
      ...(businessId && type === 'expense' && supplierName ? { supplierName } : { supplierName: undefined }),
      ...(propertyId ? { propertyId } : { propertyId: undefined }),
      ...(personalProjectId ? { personalProjectId } : { personalProjectId: undefined }),
      ...(recurrence !== 'none' ? { recurrence } : { recurrence: undefined }),
    };
    if (isEdit) {
      updateTransaction(transaction.id, data);
    } else {
      addTransaction(data);
      if (linkedToId) {
        const latest = useStore.getState().transactions[0];
        if (latest) linkTransactions(latest.id, linkedToId);
      }
    }
    draft.clear();
    handleClose();
  };

  const handleClose = () => {
    setAmount('');
    setCategory('');
    setDescription('');
    setDate(new Date().toISOString().split('T')[0]);
    setMemberIds([]);
    setUnitId('');
    setLinkedToId('');
    setShowLinkSection(false);
    setBusinessId('');
    setProjectId('');
    setClientName('');
    setSupplierName('');
    setPropertyId('');
    setPersonalProjectId('');
    setRecurrence('none');
    setErrors({});
    setType(defaultType);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} title={isEdit ? t('transactions.form.editTransaction') : type === 'income' ? t('transactions.form.addIncome') : t('transactions.form.addExpense')} footer={
      <Button
        className="w-full"
        variant={type === 'income' ? 'primary' : 'primary'}
        onClick={handleSave}
      >
        {t('common.save')}
      </Button>
    }>
      <div className="space-y-4">
        {/* Type toggle + scan button */}
        <div className="flex gap-2 items-center">
          <div className="flex gap-2 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl flex-1">
          {['expense', 'income'].map((t_) => (
            <button
              key={t_}
              onClick={() => { setType(t_); setCategory(''); }}
              className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                type === t_
                  ? t_ === 'income'
                    ? 'bg-success-500 text-white'
                    : 'bg-brand-600 text-white'
                  : 'text-gray-500'
              }`}
            >
              {t_ === 'income' ? t('common.income') : t('common.expense')}
            </button>
          ))}
          </div>
          {onScanReceipt && !isEdit && (
            <button
              onClick={() => { handleClose(); onScanReceipt(); }}
              className="p-2.5 rounded-xl bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400 hover:bg-brand-100 dark:hover:bg-brand-500/20 transition-colors"
              title={t('scan.scanReceipt')}
            >
              <Camera size={20} />
            </button>
          )}
        </div>

        {/* Amount */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('transactions.form.amount')}
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-bold text-gray-300">$</span>
            <CurrencyInput
              placeholder="0"
              value={amount}
              onChange={setAmount}
              currencyOverride={businessId ? businesses.find((b) => b.id === businessId)?.currency : undefined}
              className={`w-full pl-10 pr-4 py-4 text-2xl font-bold input ${
                errors.amount ? 'border-danger-500' : ''
              }`}
            />
          </div>
          {errors.amount && <p className="text-xs text-danger-500 mt-1">{errors.amount}</p>}
        </div>

        {/* Category grid */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('transactions.form.category')}
          </label>
          <div className="grid grid-cols-3 gap-2">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`py-2 px-3 text-xs font-medium rounded-xl border transition-all flex flex-col items-center gap-1 ${
                  category === c
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400'
                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-brand-400'
                }`}
              >
                <span>{CATEGORY_ICONS[c] || '📌'}</span>
                {t(`categories.${c}`)}
              </button>
            ))}
          </div>
          {errors.category && <p className="text-xs text-danger-500 mt-1">{errors.category}</p>}
        </div>

        {/* Unit selector (when category is unit_cost) */}
        {category === 'unit_cost' && incomeUnits.length > 0 && (
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
              {t('incomeUnits.title')}
            </label>
            <div className="flex gap-2 flex-wrap">
              {incomeUnits.map((u) => (
                <button
                  key={u.id}
                  onClick={() => setUnitId(unitId === u.id ? '' : u.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                    unitId === u.id
                      ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400'
                      : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-brand-400'
                  }`}
                >
                  <span>{u.icon}</span>
                  {u.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Description */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('transactions.form.description')}
          </label>
          <input
            type="text"
            className="input"
            placeholder={type === 'income' ? 'Salario, freelance...' : 'Supermercado, Uber...'}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        {/* Date */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('transactions.form.date')}
          </label>
          <input
            type="date"
            className="input"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>

        {/* Business picker */}
        {businessEnabled && businesses.length > 0 && (
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
              {t('business.assignBusiness')}
            </label>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => { setBusinessId(''); setProjectId(''); }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                  !businessId
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400'
                    : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:border-brand-400'
                }`}
              >
                {t('business.personal')}
              </button>
              {businesses.map((b) => (
                <button
                  key={b.id}
                  onClick={() => { setBusinessId(b.id); setProjectId(''); }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                    businessId === b.id
                      ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400'
                      : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-brand-400'
                  }`}
                >
                  <span>{b.icon}</span>
                  {b.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Project picker (when business selected) */}
        {businessId && (() => {
          const bizProjects = allProjects.filter((p) => p.businessId === businessId);
          if (bizProjects.length === 0) return null;
          return (
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
                {t('projects.assignProject')}
              </label>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => setProjectId('')}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                    !projectId
                      ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400'
                      : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:border-brand-400'
                  }`}
                >
                  {t('common.none')}
                </button>
                {bizProjects.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setProjectId(p.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                      projectId === p.id
                        ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400'
                        : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-brand-400'
                    }`}
                  >
                    <span>{p.icon}</span>
                    {p.name}
                  </button>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Client / Supplier (when business selected) */}
        {businessId && (
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
              {type === 'income' ? t('transactions.form.client') : t('transactions.form.supplier')}
            </label>
            <input
              type="text"
              className="input"
              placeholder={type === 'income' ? t('transactions.form.clientPlaceholder') : t('transactions.form.supplierPlaceholder')}
              value={type === 'income' ? clientName : supplierName}
              onChange={(e) => type === 'income' ? setClientName(e.target.value) : setSupplierName(e.target.value)}
            />
          </div>
        )}

        {/* Recurrence toggle */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('transactions.form.recurrence')}
          </label>
          <div className="flex gap-2">
            {['none', 'monthly'].map((r) => (
              <button
                key={r}
                onClick={() => setRecurrence(r)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                  recurrence === r
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400'
                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-brand-400'
                }`}
              >
                {r === 'monthly' && <Repeat size={12} />}
                {t(`transactions.form.recurrence${r.charAt(0).toUpperCase() + r.slice(1)}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Personal project picker (expenses, no business) */}
        {!businessId && type === 'expense' && personalProjects.length > 0 && (
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
              {t('personalProjects.assignProject')}
            </label>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setPersonalProjectId('')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                  !personalProjectId
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400'
                    : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:border-brand-400'
                }`}
              >
                {t('common.none')}
              </button>
              {personalProjects.filter((p) => p.status === 'active').map((p) => (
                <button
                  key={p.id}
                  onClick={() => setPersonalProjectId(p.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                    personalProjectId === p.id
                      ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400'
                      : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-brand-400'
                  }`}
                >
                  <span>{p.icon}</span>
                  {p.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Member picker */}
        {!businessId && (
          <MemberMultiSelect
            value={memberIds}
            onChange={setMemberIds}
            label={t('transactions.form.forMember')}
          />
        )}

        {/* Link with opposite transaction */}
        {!isEdit && (
          <div>
            <button
              type="button"
              onClick={() => setShowLinkSection(!showLinkSection)}
              className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
            >
              <Link2 size={14} />
              {t('transactions.linked.linkWith')}
              <ChevronDown size={14} className={`transition-transform ${showLinkSection ? 'rotate-180' : ''}`} />
            </button>
            {showLinkSection && (
              <div className="mt-2 flex gap-2 flex-wrap">
                {(() => {
                  const oppositeType = type === 'income' ? 'expense' : 'income';
                  const available = transactions.filter((tx) => tx.type === oppositeType && !tx.linkedTo);
                  if (available.length === 0) {
                    return (
                      <span className="text-xs text-gray-400">
                        {type === 'income' ? t('transactions.linked.selectExpense') : t('transactions.linked.selectIncome')} — {t('transactions.linked.noLinks')}
                      </span>
                    );
                  }
                  return available.map((tx) => (
                    <button
                      key={tx.id}
                      type="button"
                      onClick={() => setLinkedToId(linkedToId === tx.id ? '' : tx.id)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        linkedToId === tx.id
                          ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400'
                          : tx.type === 'income'
                            ? 'border-success-200 dark:border-success-500/30 bg-success-50 dark:bg-success-500/10 text-success-700 dark:text-success-400 hover:border-success-400'
                            : 'border-danger-200 dark:border-danger-500/30 bg-danger-50 dark:bg-danger-500/10 text-danger-700 dark:text-danger-400 hover:border-danger-400'
                      }`}
                    >
                      {tx.description} ({tx.type === 'income' ? '+' : '-'}{formatCurrency(tx.amount)})
                    </button>
                  ));
                })()}
              </div>
            )}
          </div>
        )}

      </div>
    </Modal>
  );
}
